// Pipes (a.k.a. Net / Loops) - pure logic: types, deterministic generation,
// rotation, win-check, par. No DOM imports.
//
// Solvability guarantee: a level is BUILT as a solved state (a random spanning
// tree over the grid, plus a few extra edges for difficulty) and then scrambled
// by rotating tiles. Because the solved state exists by construction, every
// level is solvable - no solver needed. Par is the sum of per-tile minimum CW
// rotations from the scrambled start back to the generated solved state.

/** Direction bitmask: N=1, E=2, S=4, W=8. */
export type Conn = number; // 0..15

export const N = 1;
export const E = 2;
export const S = 4;
export const W = 8;
const BITS: Conn[] = [N, E, S, W];

export interface PipesLevel {
  n: number; // level number (== seed source)
  w: number;
  h: number;
  /** Solved orientation per cell (bitmask), row-major. */
  solved: Conn[];
  /** Scrambled start orientation per cell. */
  start: Conn[];
  /** Minimum taps from start to the generated solved state. */
  par: number;
}

export interface PipesState {
  cells: Conn[]; // current orientations
  taps: number;
}

// ---- Rotation & symmetry ---------------------------------------------------

/** Rotate a tile 90 degrees clockwise: N->E->S->W->N. */
export function rotateCw(c: Conn): Conn {
  return ((c << 1) | (c >> 3)) & 0b1111;
}

/** The bit facing the opposite direction (N<->S, E<->W). */
function opposite(bit: Conn): Conn {
  return ((bit << 2) | (bit >> 2)) & 0b1111;
}

/** Distinct orientations a tile has under rotation (cross=1, straight=2, else 4). */
export function rotationPeriod(c: Conn): number {
  if (c === 0) return 1;
  let r = rotateCw(c);
  let p = 1;
  while (r !== c && p < 4) {
    r = rotateCw(r);
    p++;
  }
  return p;
}

/**
 * Minimum CW taps k in {0..3} so rotateCw^k(from) === to. Because only a CW tap
 * exists, this is just the smallest such k; symmetric pieces reach `to` sooner
 * (a straight piece is at most 1 away, a cross is always 0). Assumes `from` and
 * `to` are rotations of the same piece (always true within a level).
 */
export function minRotationDistance(from: Conn, to: Conn): number {
  let c = from;
  for (let k = 0; k < 4; k++) {
    if (c === to) return k;
    c = rotateCw(c);
  }
  return 0;
}

// ---- Grid helpers ----------------------------------------------------------

function neighborsOf(i: number, w: number, h: number): Array<{ idx: number; bit: Conn }> {
  const r = Math.floor(i / w);
  const c = i % w;
  const out: Array<{ idx: number; bit: Conn }> = [];
  if (r > 0) out.push({ idx: i - w, bit: N });
  if (c < w - 1) out.push({ idx: i + 1, bit: E });
  if (r < h - 1) out.push({ idx: i + w, bit: S });
  if (c > 0) out.push({ idx: i - 1, bit: W });
  return out;
}

/** Index of the neighbor across `bit`, or -1 if it runs off the board. */
function stepIndex(i: number, bit: Conn, w: number, h: number): number {
  const r = Math.floor(i / w);
  const c = i % w;
  if (bit === N) return r > 0 ? i - w : -1;
  if (bit === E) return c < w - 1 ? i + 1 : -1;
  if (bit === S) return r < h - 1 ? i + w : -1;
  if (bit === W) return c > 0 ? i - 1 : -1;
  return -1;
}

// ---- Win / progress checks -------------------------------------------------

/**
 * True win state: every set bit matches a bit at the neighbor (nothing runs off
 * the board), AND the whole grid is one connected network. NOT compared with
 * `solved`, so alternative valid end states all count.
 */
export function isSolved(level: PipesLevel, cells: Conn[]): boolean {
  const { w, h } = level;
  const size = w * h;
  for (let i = 0; i < size; i++) {
    for (const bit of BITS) {
      if (cells[i] & bit) {
        const nb = stepIndex(i, bit, w, h);
        if (nb < 0 || !(cells[nb] & opposite(bit))) return false;
      }
    }
  }
  // Connectivity: flood fill across matched edges must reach every cell.
  const seen = new Array<boolean>(size).fill(false);
  const stack = [0];
  seen[0] = true;
  let count = 1;
  while (stack.length) {
    const cur = stack.pop()!;
    for (const bit of BITS) {
      if (cells[cur] & bit) {
        const nb = stepIndex(cur, bit, w, h);
        if (nb >= 0 && !seen[nb]) {
          seen[nb] = true;
          count++;
          stack.push(nb);
        }
      }
    }
  }
  return count === size;
}

/** Are all of this tile's connections reciprocated by neighbors? */
export function cellMatched(level: PipesLevel, cells: Conn[], i: number): boolean {
  const { w, h } = level;
  for (const bit of BITS) {
    if (cells[i] & bit) {
      const nb = stepIndex(i, bit, w, h);
      if (nb < 0 || !(cells[nb] & opposite(bit))) return false;
    }
  }
  return true;
}

/** Half-edge match count over the whole board (for the HUD percentage). */
export function matchedEdges(level: PipesLevel, cells: Conn[]): { matched: number; total: number } {
  const { w, h } = level;
  const size = w * h;
  let matched = 0;
  let total = 0;
  for (let i = 0; i < size; i++) {
    for (const bit of BITS) {
      if (cells[i] & bit) {
        total++;
        const nb = stepIndex(i, bit, w, h);
        if (nb >= 0 && cells[nb] & opposite(bit)) matched++;
      }
    }
  }
  return { matched, total };
}

// ---- Deterministic RNG (mulberry32) ---------------------------------------

function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- Difficulty curve ------------------------------------------------------

export interface PipesShape {
  w: number;
  h: number;
  extra: number; // extra (cycle) edges beyond the spanning tree
}

/**
 * Grid size and cycle count as a function of level number. Small and tree-only
 * during onboarding; cycles (T-junctions / crosses) arrive later. Width stays
 * <= 7 so the board fits a portrait phone (<=480px). Thresholds may be tuned
 * after playtesting, but the shape (start small, cycles after onboarding) is
 * fixed.
 */
export function shapeForPipesLevel(n: number): PipesShape {
  if (n <= 5) return { w: 4, h: 4, extra: 0 };
  if (n <= 12) return { w: 5, h: 5, extra: 1 };
  if (n <= 25) return { w: 5, h: 6, extra: 2 };
  if (n <= 45) return { w: 6, h: 7, extra: 3 };
  // 46+: grow rows every 25 levels (6x8 -> 6x9 -> 7x9), then cap at 7x9.
  const steps = Math.floor((n - 46) / 25);
  const h = 8 + Math.min(steps, 1);
  const w = steps >= 2 ? 7 : 6;
  return { w, h, extra: Math.min(6, 3 + Math.floor(n / 40)) };
}

// ---- Generation ------------------------------------------------------------

/** Generate one always-solvable level deterministically from its number. */
export function generatePipesLevel(n: number): PipesLevel {
  const { w, h, extra } = shapeForPipesLevel(n);
  const size = w * h;
  const rng = makeRng((5000 + n * 2654435761) >>> 0);
  const solved: Conn[] = new Array<Conn>(size).fill(0);

  // 1. Random spanning tree via randomized iterative DFS.
  const visited = new Array<boolean>(size).fill(false);
  const stack = [0];
  visited[0] = true;
  while (stack.length) {
    const cur = stack[stack.length - 1];
    const opts = neighborsOf(cur, w, h).filter((nb) => !visited[nb.idx]);
    if (opts.length === 0) {
      stack.pop();
      continue;
    }
    const pick = opts[Math.floor(rng() * opts.length)];
    solved[cur] |= pick.bit;
    solved[pick.idx] |= opposite(pick.bit);
    visited[pick.idx] = true;
    stack.push(pick.idx);
  }

  // 2. Add extra (cycle) edges -> T-junctions and crosses.
  const walls: Array<{ a: number; bit: Conn; b: number }> = [];
  for (let i = 0; i < size; i++) {
    for (const nb of neighborsOf(i, w, h)) {
      if (nb.idx > i && !(solved[i] & nb.bit)) walls.push({ a: i, bit: nb.bit, b: nb.idx });
    }
  }
  for (let i = walls.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [walls[i], walls[j]] = [walls[j], walls[i]];
  }
  const addCount = Math.min(extra, walls.length);
  for (let k = 0; k < addCount; k++) {
    const wl = walls[k];
    solved[wl.a] |= wl.bit;
    solved[wl.b] |= opposite(wl.bit);
  }

  // 3. Scramble, with a re-scramble guarantee so no level is trivially easy.
  const nonSym = solved.filter((c) => rotationPeriod(c) > 1).length;
  const threshold = Math.max(1, Math.floor(nonSym * 0.4));
  let best: { start: Conn[]; par: number } | null = null;
  let start: Conn[] = solved.slice();
  let par = 0;
  for (let attempt = 0; attempt < 40; attempt++) {
    const cand = solved.map((c) => {
      const p = rotationPeriod(c);
      let k = Math.floor(rng() * p);
      let s = c;
      while (k-- > 0) s = rotateCw(s);
      return s;
    });
    let d = 0;
    for (let i = 0; i < size; i++) d += minRotationDistance(cand[i], solved[i]);
    if (d >= threshold) {
      start = cand;
      par = d;
      best = null;
      break;
    }
    if (!best || d > best.par) best = { start: cand, par: d };
  }
  if (best) {
    start = best.start;
    par = best.par;
  }

  return { n, w, h, solved, start, par };
}
